import React, { useState, useEffect } from "react";
import {
  FiMapPin,
  FiLayers,
  FiCalendar,
  FiFilter,
  FiX,
  FiChevronDown,
  FiCheck,
  FiHeart,
  FiShare2,
  FiEye,
  FiHome,
  FiGrid,
  FiList,
  FiArrowRight,
  FiStar,
  FiTrendingUp,
  FiPercent,
  FiClock,
  FiZoomIn,
  FiMaximize,
  FiMinimize,
  FiMinus,
  FiPlus,
  FiDownload
} from "react-icons/fi";
import { FaRupeeSign, FaWhatsapp, FaPhoneAlt, FaRegHeart, FaHeart, FaBuilding } from "react-icons/fa";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { sendApiRequest } from "../utils";
import defaultPlotImage from "../assets/images/logo_new.jpeg";

const PlotAvailability = () => {
    const [viewMode, setViewMode] = useState("map");
    const [selectedPlot, setSelectedPlot] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [mapFilter, setMapFilter] = useState("all");
    const [wishlist, setWishlist] = useState([]);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [plots, setPlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [siteViewMode, setSiteViewMode] = useState("interactive"); // interactive or pdf
    const [pdfZoom, setPdfZoom] = useState(100);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await sendApiRequest("api/packages", {}, "GET");

      if (response.data) {
        const packages = Array.isArray(response.data) ? response.data : [];
        const formattedPlots = packages.map((pkg, index) => ({
          id: pkg._id || index,
          _id: pkg._id,
          number: pkg.plotNumber || `${String.fromCharCode(65 + ((pkg.row || 1) - 1))}${(pkg.col || 1)}`,
          row: (pkg.row || 1) - 1, // Convert from 1-based to 0-based
          col: (pkg.col || 1) - 1, // Convert from 1-based to 0-based
          width: pkg.gridWidth || 1,
          height: pkg.gridHeight || 1,
          size: pkg.sizeCategory || "medium",
          area: `${pkg.width || 30}' x ${pkg.length || 30}' = ${(pkg.width || 30) * (pkg.length || 30)} sq.ft`,
          dimensions: `${pkg.width || 30}' x ${pkg.length || 30}'`,
          price: formatPrice(pkg.price),
          status: pkg.plotStatus || "available",
          facing: pkg.facing || "North",
          location: pkg.location || "Prime Location",
          title: pkg.title,
          description: pkg.description || `Beautiful ${pkg.facing || "North"} facing plot available for sale.`,
          features: pkg.features || ['Wide Road Access', '24/7 Security', 'Electricity Connection', 'Water Supply'],
          nearby: pkg.nearby || ['Metro Station', 'School', 'Hospital', 'Shopping Mall'],
          photos: (pkg.photos && pkg.photos.length > 0)
            ? pkg.photos
            : [defaultPlotImage],
          isCorner: pkg.isCorner || false,
          isPremium: pkg.isPremium || false,
          badge: pkg.isPremium ? "Premium" : pkg.isCorner ? "Corner Plot" : null
        }));
        setPlots(formattedPlots);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "₹0";
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} Lakh`;
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'from-green-400 to-green-600';
      case 'sold': return 'from-red-400 to-red-600';
      case 'booked': return 'from-yellow-400 to-yellow-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'available': return 'Available';
      case 'sold': return 'Sold';
      case 'booked': return 'Booked';
      default: return 'N/A';
    }
  };

  const filteredPlots = mapFilter === 'all' ? plots : plots.filter(plot => plot.status === mapFilter);

  const availableCount = plots.filter(p => p.status === 'available').length;
  const soldCount = plots.filter(p => p.status === 'sold').length;
  const bookedCount = plots.filter(p => p.status === 'booked').length;

  const toggleWishlist = (plotId) => {
    setWishlist(prev =>
      prev.includes(plotId) ? prev.filter(id => id !== plotId) : [...prev, plotId]
    );
  };

  const openPlotModal = (plot) => {
    setSelectedPlot(plot);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setShowModal(false);
    document.body.style.overflow = "auto";
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, 1.5));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, 0.7));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-500 font-medium">Loading packages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-sm font-semibold mb-4">
              Site Layout
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Master Plan – Plot Map
            </h1>
            <p className="text-gray-500 mt-4 text-lg max-w-2xl mx-auto">
              Interactive layout with actual plot sizes. Different box sizes represent actual plot dimensions.
            </p>
          </motion.div>
        </div>

        {/* Stats Bar */}
        <div className="flex flex-wrap justify-center gap-6 mb-8">
          <div className="bg-emerald-100 px-6 py-3 rounded-2xl flex items-center gap-3 shadow-md">
            <div className="w-4 h-4 rounded-full bg-green-500 animate-pulse"></div>
            <span className="font-semibold text-emerald-700">{availableCount} Available</span>
          </div>
          <div className="bg-red-100 px-6 py-3 rounded-2xl flex items-center gap-3 shadow-md">
            <div className="w-4 h-4 rounded-full bg-red-500"></div>
            <span className="font-semibold text-red-700">{soldCount} Sold</span>
          </div>
          <div className="bg-amber-100 px-6 py-3 rounded-2xl flex items-center gap-3 shadow-md">
            <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
            <span className="font-semibold text-amber-700">{bookedCount} Booked</span>
          </div>
        </div>

        {/* Filter Buttons & Controls */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
          <div className="flex gap-3">
            {['all', 'available', 'sold', 'booked'].map((filterType) => (
              <button
                key={filterType}
                onClick={() => setMapFilter(filterType)}
                className={`px-6 py-2 rounded-xl font-semibold transition-all capitalize ${mapFilter === filterType
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 shadow-md'
                  }`}
              >
                {filterType === 'all' ? 'All Plots' : `${filterType} Plots`}
              </button>
            ))}
          </div>

          {/* Zoom Controls */}
          <div className="flex gap-2">
            <button
              onClick={handleZoomOut}
              className="p-2 bg-white rounded-xl shadow-md hover:bg-gray-50 transition"
            >
              <FiMinimize className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={handleZoomIn}
              className="p-2 bg-white rounded-xl shadow-md hover:bg-gray-50 transition"
            >
              <FiMaximize className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Size Legend */}
        <div className="flex flex-wrap justify-center gap-6 mb-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded"></div>
            <span>Small Plot</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded"></div>
            <span>Medium Plot</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-8 bg-gradient-to-br from-teal-400 to-teal-600 rounded"></div>
            <span>Large Plot</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded"></div>
            <span>XL Plot</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full"></div>
            <span>Corner Plot</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-lg"></div>
            <span>Premium Plot</span>
          </div>
        </div>

        {/* Site View Mode Toggle (Interactive vs PDF) */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-gray-100 p-1 rounded-2xl shadow-inner">
            <button
              onClick={() => setSiteViewMode('interactive')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                siteViewMode === 'interactive'
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-white hover:shadow-md'
              }`}
            >
              <FiGrid className="w-4 h-4" />
              Interactive Grid
            </button>
            <button
              onClick={() => setSiteViewMode('pdf')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                siteViewMode === 'pdf'
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-white hover:shadow-md'
              }`}
            >
              <FiEye className="w-4 h-4" />
              Site Layout PDF
            </button>
          </div>
        </div>

        {/* Interactive Grid View Mode Toggle (Only visible in interactive mode */}
        {siteViewMode === 'interactive' && (
            <div className="flex justify-end gap-2 mb-6">
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 rounded-xl transition-all flex items-center gap-2 px-4 ${viewMode === 'map' ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg' : 'bg-white text-gray-600 shadow-md'}`}
              >
                <FiGrid className="w-4 h-4" />
                Map View
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-xl transition-all flex items-center gap-2 px-4 ${viewMode === 'list' ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg' : 'bg-white text-gray-600 shadow-md'}`}
              >
                <FiList className="w-4 h-4" />
                List View
              </button>
            </div>
        )}

        {/* Interactive Grid Content */}
        {siteViewMode === 'interactive' && (
            <>
                {/* Plot Map Grid View with Dynamic Sizes */}
                {viewMode === 'map' && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-3xl shadow-2xl p-6 overflow-x-auto border border-gray-100"
                >
            <div
              className="relative"
              style={{
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'top left',
                transition: 'transform 0.3s ease'
              }}
            >
              {/* Grid Container using CSS Grid with dynamic sizing */}
              <div className="grid gap-1" style={{
                gridTemplateColumns: 'auto repeat(25, minmax(60px, auto))',
                minWidth: '1600px'
              }}>
                {/* Column Labels */}
                <div className="h-8"></div>
                {[...Array(25)].map((_, col) => (
                  <div key={col} className="text-center font-bold text-indigo-600 text-xs py-2">
                    Col {col + 1}
                  </div>
                ))}

                {/* Plot Grid - Group plots by row */}
                {[...Array(60)].map((_, rowIndex) => {
                  return (
                    <React.Fragment key={rowIndex}>
                      {/* Row Label */}
                      <div className="font-bold text-indigo-600 text-center bg-indigo-50 rounded-xl flex items-center justify-center h-16">
                        {String.fromCharCode(65 + rowIndex)}
                      </div>

                      {/* Create grid cells for this row */}
                      {[...Array(25)].map((_, colIndex) => {
                        const allPlotsPlot = plots.find(p => p.row === rowIndex && p.col === colIndex);
                        const plot = filteredPlots.find(p => p.row === rowIndex && p.col === colIndex);

                        if (!allPlotsPlot) {
                          // Empty cell (no plot here at all)
                          return (
                            <div key={`${rowIndex}-${colIndex}`} className="bg-gray-50 rounded-lg min-h-[60px] border border-dashed border-gray-200"></div>
                          );
                        }

                        if (!plot) {
                          // Plot exists but is filtered out, show empty cell
                          return (
                            <div key={`${rowIndex}-${colIndex}`} className="bg-gray-50 rounded-lg min-h-[60px] border border-dashed border-gray-200"></div>
                          );
                        }

                        // Check if this is the starting cell of a multi-cell plot
                        const isStartingCell = plot.col === colIndex;

                        if (!isStartingCell) {
                          return null; // Skip cells that are part of a larger plot but not the start
                        }

                        // Calculate column span
                        const colSpan = plot.width;
                        const rowSpan = plot.height;

                        // Custom styling based on plot size
                        const getSizeClasses = () => {
                          switch (plot.size) {
                            case 'small':
                              return 'rounded-lg';
                            case 'medium':
                              return 'rounded-xl';
                            case 'large':
                              return 'rounded-xl shadow-md';
                            case 'xl':
                              return 'rounded-2xl shadow-lg';
                            case 'corner':
                              return 'rounded-full';
                            case 'premium':
                              return 'rounded-2xl shadow-xl ring-2 ring-indigo-300';
                            default:
                              return 'rounded-xl';
                          }
                        };

                        return (
                          <div
                            key={`${rowIndex}-${colIndex}`}
                            style={{
                              gridColumn: `span ${colSpan}`,
                              gridRow: `span ${rowSpan}`
                            }}
                            className={`relative group cursor-pointer transition-all duration-200 hover:scale-[1.02] ${getSizeClasses()}`}
                          >
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => openPlotModal(plot)}
                              className={`w-full h-full min-h-[60px] bg-gradient-to-br ${getStatusColor(plot.status)} text-white p-2 flex flex-col items-center justify-center transition-all shadow-md hover:shadow-xl ${getSizeClasses()}`}
                            >
                              <span className="text-xs font-bold opacity-90 group-hover:scale-110 transition">
                                {plot.number}
                              </span>
                              <span className="text-[10px] opacity-75 mt-1 hidden sm:block">
                                {plot.dimensions}
                              </span>

                              {/* Corner/Premium Badge */}
                              {(plot.isCorner || plot.isPremium) && (
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-pulse"></div>
                              )}

                              {/* Status Indicator */}
                              <div className="absolute bottom-1 right-1 w-2 h-2 rounded-full bg-white opacity-75"></div>
                            </motion.button>

                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition pointer-events-none z-20">
                              Plot {plot.number}: {getStatusText(plot.status)} | {plot.area} | {plot.price}
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-8 pt-6 border-t border-gray-200 flex flex-wrap justify-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded"></div>
                <span className="text-gray-700">Small</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded"></div>
                <span className="text-gray-700">Medium</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-12 h-8 bg-gradient-to-br from-teal-400 to-teal-600 rounded"></div>
                <span className="text-gray-700">Large</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded"></div>
                <span className="text-gray-700">XL</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full"></div>
                <span className="text-gray-700">Corner Plot</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-lg"></div>
                <span className="text-gray-700">Premium Plot</span>
              </div>
            </div>

            {/* Map Information */}
            <div className="mt-6 p-4 bg-indigo-50 rounded-xl">
              <div className="flex items-center gap-2 text-indigo-700">
                <FiMapPin className="w-4 h-4" />
                <span className="text-sm font-medium">Master Plan Reference: DP-2024-001 | Total Area: 35 Acres | Plots from packages | Approved by Local Authority</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* List View - Plot Details */}
        {viewMode === 'list' && (
          <div className="space-y-4">
            {filteredPlots.map((plot, index) => (
              <motion.div
                key={plot.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all cursor-pointer"
                onClick={() => openPlotModal(plot)}
              >
                <div className="flex flex-col md:flex-row">
                  <div className={`md:w-48 h-32 relative overflow-hidden ${plot.size === 'small' ? 'bg-gradient-to-br from-green-100 to-green-200' :
                      plot.size === 'medium' ? 'bg-gradient-to-br from-emerald-100 to-emerald-200' :
                        plot.size === 'large' ? 'bg-gradient-to-br from-teal-100 to-teal-200' :
                          plot.size === 'xl' ? 'bg-gradient-to-br from-blue-100 to-blue-200' :
                            plot.isCorner ? 'bg-gradient-to-br from-purple-100 to-purple-200' :
                              'bg-gradient-to-br from-indigo-100 to-indigo-200'
                    }`}>
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <span className="text-3xl font-bold text-gray-500">{plot.number}</span>
                      <span className="text-xs text-gray-500 mt-1">{plot.area}</span>
                    </div>
                    <div className={`absolute top-2 left-2 w-3 h-3 rounded-full ${plot.status === 'available' ? 'bg-green-500' : plot.status === 'sold' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                    {plot.isCorner && <div className="absolute top-2 right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs">C</div>}
                    {plot.isPremium && <div className="absolute top-2 right-2 w-6 h-6 bg-indigo-500 rounded-lg flex items-center justify-center text-white text-xs">P</div>}
                  </div>
                  <div className="flex-1 p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-lg text-gray-800">{plot.title || `Plot ${plot.number}`}</h3>
                        <p className="text-gray-500 text-sm flex items-center gap-1">
                          <FiMapPin className="w-3 h-3" />
                          {plot.location}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${plot.status === 'available' ? 'bg-green-100 text-green-700' :
                          plot.status === 'sold' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                        {getStatusText(plot.status)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-3">
                      <div>
                        <p className="text-xs text-gray-400">Area</p>
                        <p className="font-semibold text-gray-800">{plot.area}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Dimensions</p>
                        <p className="font-semibold text-gray-800">{plot.dimensions}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Price</p>
                        <p className="font-semibold text-indigo-600">{plot.price}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Facing</p>
                        <p className="font-semibold text-gray-800">{plot.facing}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
                )}
            </>
        )}

        {/* PDF Site Layout View */}
        {siteViewMode === 'pdf' && (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl shadow-2xl p-8 overflow-hidden border border-gray-100"
            >
                {/* PDF Controls */}
                <div className="flex flex-wrap gap-4 items-center justify-between mb-6 pb-6 border-b border-gray-100">
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <FiEye className="text-indigo-600" />
                            Site Layout Master Plan
                        </h3>
                        <p className="text-gray-500 mt-1">
                            Official site plan and development layout
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Zoom Controls */}
                        <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1">
                            <button
                                onClick={() => setPdfZoom(prev => Math.max(50, prev - 10))}
                                className="p-2 rounded-lg hover:bg-white hover:shadow transition-all"
                            >
                                <FiMinus className="w-5 h-5 text-gray-700" />
                            </button>
                            <span className="font-semibold text-gray-700 w-12 text-center">{pdfZoom}%</span>
                            <button
                                onClick={() => setPdfZoom(prev => Math.min(200, prev + 10))}
                                className="p-2 rounded-lg hover:bg-white hover:shadow transition-all"
                            >
                                <FiPlus className="w-5 h-5 text-gray-700" />
                            </button>
                        </div>
                        {/* Download Button */}
                        <a
                            href="/site_layout.pdf"
                            download="site_layout.pdf"
                            className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all"
                        >
                            <FiDownload className="w-5 h-5" />
                            Download PDF
                        </a>
                    </div>
                </div>
                {/* PDF Viewer */}
                <div className="relative bg-gray-50 rounded-2xl overflow-hidden border border-gray-200">
                    <div className="flex justify-center">
                        <iframe
                            src="/site_layout.pdf#toolbar=1"
                            style={{
                                width: `${pdfZoom}%`,
                                height: '800px',
                                transform: `scale(${pdfZoom / 100})`,
                                transformOrigin: 'top center',
                                transition: 'transform 0.3s ease'
                            }}
                            className="rounded"
                            title="Site Layout"
                        />
                    </div>
                </div>
            </motion.div>
        )}

        {/* Plot Detail Modal */}
        <AnimatePresence>
          {showModal && selectedPlot && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={closeModal}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold text-gray-800">{selectedPlot.title || `Plot ${selectedPlot.number}`}</h2>
                      {(selectedPlot.isCorner || selectedPlot.isPremium) && (
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${selectedPlot.isCorner ? 'bg-purple-100 text-purple-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          {selectedPlot.isCorner ? 'Corner Plot' : 'Premium Plot'}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                      <FiMapPin className="w-3 h-3" />
                      {selectedPlot.location}
                    </p>
                  </div>
                  <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full transition">
                    <FiX className="w-6 h-6 text-gray-500" />
                  </button>
                </div>

                <div className="p-6">
                  {/* Size Indicator */}
                  <div className={`mb-6 p-4 rounded-xl flex items-center justify-between ${selectedPlot.size === 'small' ? 'bg-green-50 border border-green-200' :
                      selectedPlot.size === 'medium' ? 'bg-emerald-50 border border-emerald-200' :
                        selectedPlot.size === 'large' ? 'bg-teal-50 border border-teal-200' :
                          selectedPlot.size === 'xl' ? 'bg-blue-50 border border-blue-200' :
                            selectedPlot.isCorner ? 'bg-purple-50 border border-purple-200' :
                              'bg-indigo-50 border border-indigo-200'
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg ${selectedPlot.size === 'small' ? 'bg-green-500' :
                          selectedPlot.size === 'medium' ? 'bg-emerald-500' :
                            selectedPlot.size === 'large' ? 'bg-teal-500' :
                              selectedPlot.size === 'xl' ? 'bg-blue-500' :
                                selectedPlot.isCorner ? 'bg-purple-500' : 'bg-indigo-500'
                        }`}></div>
                      <div>
                        <p className="text-sm text-gray-500">Plot Size Category</p>
                        <p className="font-semibold text-gray-800 capitalize">{selectedPlot.size} Plot • {selectedPlot.area}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Dimensions</p>
                      <p className="font-semibold text-gray-800">{selectedPlot.dimensions}</p>
                    </div>
                  </div>

                  {/* Status Banner */}
                  <div className={`mb-6 p-4 rounded-xl ${selectedPlot.status === 'available' ? 'bg-green-50 border border-green-200' :
                      selectedPlot.status === 'sold' ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${selectedPlot.status === 'available' ? 'bg-green-500' : selectedPlot.status === 'sold' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                      <span className={`font-semibold ${selectedPlot.status === 'available' ? 'text-green-700' :
                          selectedPlot.status === 'sold' ? 'text-red-700' : 'text-yellow-700'
                        }`}>
                        Status: {getStatusText(selectedPlot.status)}
                      </span>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-semibold text-lg mb-3 text-gray-800">Plot Details</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500">Plot Number</span>
                            <span className="font-semibold text-gray-800">{selectedPlot.number}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500">Area</span>
                            <span className="font-semibold text-gray-800">{selectedPlot.area}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500">Dimensions</span>
                            <span className="font-semibold text-gray-800">{selectedPlot.dimensions}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500">Price</span>
                            <span className="font-semibold text-2xl text-indigo-600">{selectedPlot.price}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500">Facing</span>
                            <span className="font-semibold text-gray-800">{selectedPlot.facing}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-semibold text-lg mb-3 text-gray-800">Description</h4>
                        <p className="text-gray-600 leading-relaxed">{selectedPlot.description}</p>
                      </div>

                      <div>
                        <h4 className="font-semibold text-lg mb-3 text-gray-800">Key Features</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedPlot.features?.map((feature, i) => (
                            <span key={i} className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-xl text-sm">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-lg mb-3 text-gray-800">Nearby Places</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedPlot.nearby?.map((place, i) => (
                            <span key={i} className="bg-gray-100 text-gray-600 px-3 py-1 rounded-xl text-sm flex items-center gap-1">
                              <FiMapPin className="w-3 h-3" /> {place}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-100">
                        <div className="flex gap-3">
                          {selectedPlot.status === 'available' ? (
                            <>
                              <Link
                                to="/contact-us"
                                className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-semibold text-center hover:shadow-lg transition-all"
                              >
                                <FaWhatsapp className="inline mr-2" /> Enquire Now
                              </Link>
                              <button className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition">
                                <FaPhoneAlt className="inline" />
                              </button>
                            </>
                          ) : (
                            <button className="w-full py-3 bg-gray-300 text-gray-500 rounded-xl font-semibold cursor-not-allowed">
                              Not Available
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PlotAvailability;
